import React from 'react';
import { useQuery } from '@tanstack/react-query';

import type { SearchConnectionLogsParams, SearchConnectionLogsResponse, UseQueryWrapper } from 'app/types';
import connectionService from '../../../Domain/Connections/Services/ConnectionService';
import { useSnakeBar } from '../../useSnakeBar';

type SearchLogsQueryVariables = SearchConnectionLogsParams;

const queryFn =
  ({ connectionId, correlationId, requester, page, pageSize }: SearchLogsQueryVariables) =>
  async () => {
    if (connectionId && correlationId) {
      const response = await connectionService.searchConnectionLogs({
        connectionId,
        correlationId,
        requester,
        page: page ?? 1,
        pageSize: pageSize ?? 10,
      });
      return response.data;
    }
  };

const QUERY_KEY = 'searchConnectionLogs';

export const useConnectionSearchLogsQuery: UseQueryWrapper<SearchConnectionLogsResponse, SearchLogsQueryVariables> = ({
  variables,
  options,
}) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<SearchConnectionLogsResponse>(
    [
      QUERY_KEY,
      variables.connectionId,
      variables.requester,
      variables.correlationId,
      variables.page,
      variables.pageSize,
    ],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      // Without a correlationId the search would return every log of the requester
      enabled: !!variables.connectionId && !!variables.correlationId,
      ...(options || {}),
    },
  );
};
